import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { X, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface CalendlyEmbedProps {
  isOpen: boolean;
  onClose: () => void;
  prefillData?: {
    firstName?: string;
    email?: string;
    companyName?: string;
    industry?: string;
    currentRevenue?: string;
    targetRevenue?: string;
    priorities?: string[];
    websiteUrl?: string;
    idealCustomer?: string;
    planSummary?: string;
    source?: string;
  };
  calendlyUrl?: string;
}

declare global {
  interface Window {
    Calendly?: {
      initInlineWidget: (options: {
        url: string;
        parentElement: HTMLElement;
        prefill?: {
          name?: string;
          email?: string;
          customAnswers?: Record<string, string>;
        };
        utm?: {
          utmSource?: string;
          utmMedium?: string;
          utmCampaign?: string;
        };
      }) => void;
    };
  }
}

const CalendlyEmbed = ({ 
  isOpen, 
  onClose, 
  prefillData,
  calendlyUrl = "https://calendly.com/vincentgrowthpartners/client-call"
}: CalendlyEmbedProps) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [hasBooked, setHasBooked] = useState(false);

  // Send booking notification with all funnel data
  const sendBookingNotification = useCallback(async (eventData: any) => {
    try {
      const { error } = await supabase.functions.invoke("notify-booking-complete", {
        body: {
          eventData,
          funnelData: prefillData,
        },
      });

      if (error) {
        console.error("Error sending booking notification:", error);
      }
    } catch (err) {
      console.error("Failed to send booking notification:", err);
    }
  }, [prefillData]);

  // Handle Calendly events
  useEffect(() => {
    if (!isOpen) return;

    const handleCalendlyEvent = async (e: MessageEvent) => {
      // Only handle Calendly events
      if (e.origin !== "https://calendly.com") return;

      const { event, payload } = e.data || {};

      if (event === "calendly.event_scheduled") {
        setHasBooked(true);
        
        // Send notification with booking and funnel data
        await sendBookingNotification(payload);
        
        toast.success("Booking confirmed! Redirecting...");
        
        // Small delay then redirect
        setTimeout(() => {
          onClose();
          navigate("/thank-you-booking");
        }, 1500);
      }
    };

    window.addEventListener("message", handleCalendlyEvent);
    return () => window.removeEventListener("message", handleCalendlyEvent);
  }, [isOpen, navigate, onClose, sendBookingNotification]);

  // Load Calendly script and initialize widget
  useEffect(() => {
    if (!isOpen) return;

    setIsLoading(true);

    // Load Calendly widget script if not already loaded
    const loadCalendlyScript = () => {
      return new Promise<void>((resolve) => {
        if (document.querySelector('script[src*="calendly.com/assets/external/widget.js"]')) {
          resolve();
          return;
        }

        const script = document.createElement("script");
        script.src = "https://assets.calendly.com/assets/external/widget.js";
        script.async = true;
        script.onload = () => resolve();
        document.head.appendChild(script);
      });
    };

    // Load Calendly CSS if not already loaded
    const loadCalendlyCSS = () => {
      if (document.querySelector('link[href*="calendly.com/assets/external/widget.css"]')) {
        return;
      }

      const link = document.createElement("link");
      link.href = "https://assets.calendly.com/assets/external/widget.css";
      link.rel = "stylesheet";
      document.head.appendChild(link);
    };

    loadCalendlyCSS();
    loadCalendlyScript().then(() => {
      setIsLoading(false);
    });
  }, [isOpen]);

  // Initialize Calendly widget when container is ready
  useEffect(() => {
    if (!isOpen || isLoading) return;

    const container = document.getElementById("calendly-embed-container");
    if (!container || !window.Calendly) return;

    // Clear any existing content
    container.innerHTML = "";

    // Build URL with prefill parameters
    const urlParams = new URLSearchParams();
    if (prefillData?.firstName) urlParams.set("name", prefillData.firstName);
    if (prefillData?.email) urlParams.set("email", prefillData.email);
    
    const fullUrl = `${calendlyUrl}?${urlParams.toString()}`;

    window.Calendly.initInlineWidget({
      url: fullUrl,
      parentElement: container,
      prefill: {
        name: prefillData?.firstName,
        email: prefillData?.email,
      },
      utm: {
        utmSource: prefillData?.source || "funnel",
        utmMedium: "growth-plan",
        utmCampaign: prefillData?.industry || "general",
      },
    });
  }, [isOpen, isLoading, prefillData, calendlyUrl]);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-4xl h-[90vh] p-0 gap-0 overflow-hidden">
        <DialogHeader className="p-4 border-b bg-gradient-to-r from-primary to-primary-dark">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-white text-lg font-semibold">
              {hasBooked ? "Booking Confirmed!" : "Schedule Your Strategy Call"}
            </DialogTitle>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="text-white hover:bg-white/10"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
        </DialogHeader>

        <div className="flex-1 relative overflow-hidden">
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-background">
              <div className="text-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
                <p className="text-muted-foreground">Loading scheduler...</p>
              </div>
            </div>
          )}

          {hasBooked && (
            <div className="absolute inset-0 flex items-center justify-center bg-background z-10">
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-2">You're All Booked!</h3>
                <p className="text-muted-foreground">Redirecting to your confirmation page...</p>
              </div>
            </div>
          )}

          <div 
            id="calendly-embed-container" 
            className="h-full w-full"
            style={{ minHeight: "700px" }}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CalendlyEmbed;
